import { NavLink } from 'react-router-dom';
import { NAV_ITEMS } from '@/components/layout/Sidebar';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { cn } from '@/utils/cn';
import type { TranslationKey } from '@/constants/i18n';

/** Short labels that fit a tab-bar chip (the sidebar uses longer ones). */
const TAB_LABEL: Record<string, TranslationKey> = {
  [ROUTES.dashboard]: 'tab.overview',
  [ROUTES.history]: 'tab.history',
  [ROUTES.files]: 'tab.files',
  [ROUTES.settings]: 'tab.settings',
  [ROUTES.admin]: 'tab.admin',
};

/**
 * Phone-first primary navigation: a fixed bottom tab bar (hidden on lg+ where
 * the desktop sidebar takes over). One-tap page switching with the same items
 * as the sidebar, plus iOS safe-area padding.
 */
export function MobileNav() {
  const { isAdmin } = useAuth();
  const { t } = useI18n();

  const items = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);

  return (
    <nav
      aria-label="Primary"
      className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-surface/90 backdrop-blur-xl lg:hidden"
    >
      <div className="mx-auto flex max-w-lg items-stretch justify-around px-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 rounded-xl px-1 py-1.5 text-[10px] font-medium transition-colors focus-ring',
                isActive ? 'text-primary-300' : 'text-zinc-500 hover:text-zinc-300'
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={cn(
                    'flex h-8 w-full max-w-[64px] items-center justify-center rounded-xl transition-colors',
                    isActive && 'bg-primary-500/15'
                  )}
                >
                  <item.icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="max-w-full truncate">{t(TAB_LABEL[item.to] ?? item.key)}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
